import { Page, Text, View } from '@react-pdf/renderer'
import {
  AutoComplete,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Skeleton,
  Tooltip,
} from 'antd'
import { SUB_DRA_MAPPINGS } from 'features/indicationTriage'
import {
  InspectionSummaryData,
  MainInfo,
  SummaryFormData,
  SummaryPageData,
} from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { ReportAutoSaveIndicator } from 'pages/reporting/shared/components/ReportAutoSaveIndicator'
import { TYPE_TO_SHORT_NAME } from 'pages/reporting/shared/constants'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import { useReportAutoSave } from 'pages/reporting/shared/hooks/useReportAutoSave'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageHeader'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import {
  FormSection,
  FormSectionSubtitle,
  FormSectionTitle,
  SectionCard,
  StyledForm,
} from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { GeckoInspectionType, GeckoMeasurementUnit, GeckoSource } from 'types'

import { InspectionScope } from './subSections/summaryPageComponents/InspectionScope'
import { InspectionSummary } from './subSections/summaryPageComponents/InspectionSummary'

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

/**
 * PageWrapper - Container for the entire summary page with consistent spacing
 */
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

/**
 * HeaderBar - Compact top bar containing metrics and auto-save status
 */
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.5rem;
  flex-wrap: wrap;
`

/**
 * MetricsRow - Horizontal layout for compact metrics display
 */
const MetricsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`

/**
 * MetricItem - Individual compact metric with label and value
 */
const MetricItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`

/**
 * MetricLabel - Small uppercase label for metrics
 */
const MetricLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  white-space: nowrap;
`

/**
 * MetricValue - Emphasized value display
 */
const MetricValue = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  white-space: nowrap;
`

/**
 * MetricDivider - Vertical separator between metrics
 */
const MetricDivider = styled.div`
  width: 1px;
  height: 1.25rem;
  background: ${({ theme }) => theme.forecast.cardDividerBackground};
`

/**
 * ContentCard - Main content card with improved styling
 */
const ContentCard = styled(SectionCard)`
  && {
    .ant-card-head {
      padding: 0 1.25rem;
      min-height: 3rem;
    }

    .ant-card-head-title {
      padding: 0.75rem 0;
      font-size: 0.875rem;
    }

    .ant-card-body {
      padding: 1.25rem;
    }
  }
`

/**
 * ScopeMethodLabel - Label for individual scope methods
 */
const ScopeMethodLabel = styled.span`
  font-weight: 500;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.activeText};
`

/**
 * StyledCollapse - Customized collapse component
 */
const StyledCollapse = styled(Collapse)`
  && {
    background: transparent;
    border: none;

    .ant-collapse-item {
      border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
      border-radius: 0.375rem !important;
      margin-bottom: 0.75rem;
      background: ${({ theme }) => theme.forecast.cardBackground};
      overflow: hidden;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .ant-collapse-header {
      padding: 0.75rem 1rem !important;
      font-size: 0.8125rem;
      font-weight: 500;
      color: ${({ theme }) => theme.forecast.activeText};
      background: ${({ theme }) => theme.forecast.cardGroupBackground};
    }

    .ant-collapse-content {
      border-top: 1px solid
        ${({ theme }) => theme.forecast.cardDividerBackground};
    }

    .ant-collapse-content-box {
      padding: 1rem;
    }
  }
`

/**
 * FormFieldRow - Responsive row for form fields
 */
const FormFieldRow = styled(Row)`
  && {
    margin-bottom: 0;
  }
`

/**
 * CompactFormSection - Tighter form section for grouped fields
 */
const CompactFormSection = styled(FormSection)`
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`

const isValidTemperatureUnit = (
  unit: string
): unit is 'Celsius' | 'Fahrenheit' | 'Ambient' => {
  return unit === 'Celsius' || unit === 'Fahrenheit' || unit === 'Ambient'
}

const defaultInspectionScopes = [
  '100% of accessible piping sections',
  "the bottom 50% (3 o'clock to 9 o'clock position) of accessible piping sections",
  '100% of accessible shell area',
  '100% of accessible shell and head areas',
]

const RenderReact: React.FC = () => {
  const {
    formData,
    handleSummaryPageFinish,
    saveSummaryPageSilent,
    saveMainInfoSilent,
    handleMainInfoFinish,
    handleWeldScanDataFinish,
    scanCount,
    scanArea,
    totalUT,
    majorDamageRating,
    uniqueScanTypes,
    isLoading,
    inspections,
  } = useRautReportOps()

  // Sum all scan counts from the map
  const totalScanCount = useMemo(() => {
    if (!scanCount) return undefined
    return Array.from(scanCount.values()).reduce((sum, count) => sum + count, 0)
  }, [scanCount])

  const pautScanCount = useMemo(() => {
    let count = 0
    inspections.forEach((insp) => {
      if (insp.data.inspection_type === GeckoSource.PhasedArray.id) {
        count += scanCount.get(insp.id) ?? 0
      }
    })
    return count
  }, [inspections, scanCount])

  const initialValues = useMemo(
    () => ({
      ...formData.mainInfo,
      ...formData.summaryPageData,
      weldScanData: {
        ...formData.weldScanData,
        totalScans: formData.weldScanData?.totalScans ?? pautScanCount,
      },
    }),
    [
      formData.mainInfo,
      formData.summaryPageData,
      formData.weldScanData,
      pautScanCount,
    ]
  )

  const [form] = Form.useForm<SummaryFormData>()
  const {
    measurementSystem,
    getGeometricUnit,
    getTemperatureUnit,
    measurementPrecision,
  } = useUnitSystem()

  // Get units based on current measurement system
  const geometricUnit = getGeometricUnit()
  const temperatureUnit = getTemperatureUnit()

  // Helper function to get unit display string
  const getUnitDisplayString = (unit: GeckoMeasurementUnit): string => {
    switch (unit) {
      case GeckoMeasurementUnit.Inches:
        return 'in'
      case GeckoMeasurementUnit.Millimeters:
        return 'mm'
      case GeckoMeasurementUnit.Celsius:
        return 'Celsius'
      case GeckoMeasurementUnit.Fahrenheit:
        return 'Fahrenheit'
      default:
        return unit.id
    }
  }

  const temperatureUnitStr = getUnitDisplayString(temperatureUnit)

  const surfaceTemperature = Form.useWatch('surfaceTemperature', form)
  const formTempUnit = Form.useWatch('temperatureUnit', form)
  const material = Form.useWatch('material', form)
  const scopesByScanType = Form.useWatch('scopesByScanType', form)
  const inspectionScope = Form.useWatch('inspectionScope', form)
  const inspectionSummary = Form.useWatch('inspectionSummary', form)

  const isLocked = formData.lockState?.isLocked ?? false

  // Track if form has been initialized with data (to prevent auto-save during init)
  const [isFormInitialized, setIsFormInitialized] = useState(false)
  // Track the last synced data to detect when we need to re-sync the form
  const lastSyncedDataRef = useRef<string>('')

  // Auto-save data - combines MainInfo and SummaryPageData fields
  const autoSaveData = useMemo(
    () => ({
      // MainInfo fields
      surfaceTemperature,
      temperatureUnit: formTempUnit,
      material,
      scopesByScanType,
      // SummaryPageData fields
      inspectionScope,
      inspectionSummary,
    }),
    [
      surfaceTemperature,
      formTempUnit,
      material,
      scopesByScanType,
      inspectionScope,
      inspectionSummary,
    ]
  )

  // Auto-save handler - saves both MainInfo and SummaryPageData
  const handleAutoSave = useCallback(
    async (data: typeof autoSaveData) => {
      // Separate MainInfo fields
      const mainInfoData: Partial<MainInfo> = {
        surfaceTemperature:
          data.temperatureUnit === 'Ambient'
            ? undefined
            : data.surfaceTemperature,
        temperatureUnit: data.temperatureUnit,
        material: data.material,
        scopesByScanType: data.scopesByScanType,
      }

      // Separate SummaryPageData fields
      const summaryData: SummaryPageData = {
        inspectionScope: data.inspectionScope,
        inspectionSummary: data.inspectionSummary,
      }

      // Save both sections
      await Promise.all([
        saveMainInfoSilent(mainInfoData),
        saveSummaryPageSilent(summaryData),
      ])
    },
    [saveMainInfoSilent, saveSummaryPageSilent]
  )

  // Auto-save hook - only enable after form is initialized
  const { autoSaveState, saveNow } = useReportAutoSave(autoSaveData, {
    enabled: !isLoading && !isLocked && isFormInitialized,
    debounceMs: 1500,
    isLocked,
    onSave: handleAutoSave,
  })

  const hasPAUT = useMemo(
    () => uniqueScanTypes.includes(GeckoSource.PhasedArray.id),
    [uniqueScanTypes]
  )

  // Sync form with initialValues when data changes
  // This handles the case where formData updates after isLoading becomes false
  useEffect(() => {
    if (isLoading) return

    // Create a stable key from the data we care about
    const dataKey = JSON.stringify({
      material: initialValues.material,
      temperatureUnit: initialValues.temperatureUnit,
      surfaceTemperature: initialValues.surfaceTemperature,
      scopesByScanType: initialValues.scopesByScanType,
    })

    // Skip if we've already synced this exact data
    if (dataKey === lastSyncedDataRef.current) return

    const formattedInitialValues = { ...initialValues }

    // Set temperature unit if not already set
    if (formattedInitialValues.temperatureUnit === undefined) {
      formattedInitialValues.temperatureUnit = temperatureUnitStr as
        | 'Celsius'
        | 'Fahrenheit'
        | 'Ambient'
    }

    form.setFieldsValue(formattedInitialValues)
    lastSyncedDataRef.current = dataKey

    // Delay enabling auto-save to allow Form.useWatch to sync
    if (!isFormInitialized) {
      setTimeout(() => {
        setIsFormInitialized(true)
      }, 100)
    }
  }, [form, initialValues, temperatureUnitStr, isLoading, isFormInitialized])

  // Update displayed units when measurement system changes
  useEffect(() => {
    if (!form) return

    // Force a form re-render to update displayed values with current unit system
    form.setFieldsValue({ ...form.getFieldsValue() })
  }, [measurementSystem, form])

  const handleInspectionScopeChange = useCallback(
    (scope: string) => {
      form.setFieldValue('inspectionScope', scope)
    },
    [form]
  )

  const handleInspectionScopeGenerate = useCallback(async () => {
    // Get all form values including child component data
    const currentValues = form.getFieldsValue()
    const inspectionScopeValue = form.getFieldValue('inspectionScope')
    const inspectionSummaryValue = form.getFieldValue(
      'inspectionSummary'
    ) as InspectionSummaryData

    const completeValues = {
      ...currentValues,
      inspectionScope: inspectionScopeValue,
      inspectionSummary: inspectionSummaryValue,
    }

    // Save both SummaryPageData and MainInfo fields
    await handleSummaryPageFinish(completeValues)
    await handleMainInfoFinish(completeValues)
  }, [form, handleSummaryPageFinish, handleMainInfoFinish])

  const handleInspectionSummaryChange = useCallback(
    (summaryData: InspectionSummaryData) => {
      form.setFieldValue('inspectionSummary', summaryData)
    },
    [form]
  )

  const handleInspectionSummaryGenerate = useCallback(async () => {
    // Get all form values including child component data
    const currentValues = form.getFieldsValue()
    const inspectionScopeValue = form.getFieldValue('inspectionScope')
    const inspectionSummaryValue = form.getFieldValue(
      'inspectionSummary'
    ) as InspectionSummaryData

    const completeValues = {
      ...currentValues,
      inspectionScope: inspectionScopeValue,
      inspectionSummary: inspectionSummaryValue,
    }

    // Save both SummaryPageData and MainInfo fields
    await handleSummaryPageFinish(completeValues)
    await handleMainInfoFinish(completeValues)
  }, [form, handleSummaryPageFinish, handleMainInfoFinish])

  const handleFormSubmit = useCallback(
    async (values: SummaryFormData) => {
      try {
        // Create GeckoMeasurement objects for numeric values
        const formattedValues = { ...values }

        // Handle temperature
        if (formattedValues.temperatureUnit === 'Ambient') {
          formattedValues.surfaceTemperature = undefined
        }

        // Get the inspection scope and summary data
        const inspectionScopeValue = form.getFieldValue('inspectionScope')
        const inspectionSummaryValue = form.getFieldValue(
          'inspectionSummary'
        ) as InspectionSummaryData
        const weldScanData = form.getFieldValue('weldScanData')

        // Create the complete values to submit
        const completeValues: SummaryPageData & MainInfo = {
          ...initialValues,
          ...formattedValues,
          inspectionScope: inspectionScopeValue,
          inspectionSummary: inspectionSummaryValue,
        }

        await handleSummaryPageFinish(completeValues)
        await handleMainInfoFinish(completeValues)

        if (hasPAUT && weldScanData?.totalScans !== undefined) {
          await handleWeldScanDataFinish({
            totalScans: weldScanData.totalScans,
            scans: formData.weldScanData?.scans || [],
          })
        }
      } catch (error) {
        console.error('Error submitting summary form:', error)
        message.error('Failed to save summary page')
      }
    },
    [
      form,
      handleMainInfoFinish,
      handleSummaryPageFinish,
      handleWeldScanDataFinish,
      initialValues,
      hasPAUT,
      formData.weldScanData?.scans,
    ]
  )

  // Format coverage percentage
  const coverageDisplay = useMemo(() => {
    if (scanArea === undefined || scanArea === null) return 'N/A'
    return `${(scanArea * 100).toFixed(1)}%`
  }, [scanArea])

  // Format damage rating
  const damageRatingDisplay = useMemo(() => {
    if (!majorDamageRating) return 'None'
    return `${SUB_DRA_MAPPINGS[majorDamageRating]}`
  }, [majorDamageRating])

  // Show loading skeleton until data is ready
  if (isLoading) {
    return (
      <PageWrapper>
        <HeaderBar>
          <Skeleton.Input active style={{ width: 400 }} />
        </HeaderBar>
        <ContentCard title='Basic Information'>
          <Skeleton active paragraph={{ rows: 2 }} />
        </ContentCard>
        <ContentCard title='Inspection Scope'>
          <Skeleton active paragraph={{ rows: 4 }} />
        </ContentCard>
        <ContentCard title='Inspection Summary'>
          <Skeleton active paragraph={{ rows: 4 }} />
        </ContentCard>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      {/* Header Bar with Metrics and Auto-save */}
      <HeaderBar>
        <MetricsRow>
          <MetricItem>
            <MetricLabel>Scans</MetricLabel>
            <MetricValue>{totalScanCount ?? 'N/A'}</MetricValue>
          </MetricItem>
          <MetricDivider />
          <MetricItem>
            <MetricLabel>Readings</MetricLabel>
            <MetricValue>
              {totalUT !== undefined && totalUT !== null
                ? totalUT.toLocaleString()
                : 'N/A'}
            </MetricValue>
          </MetricItem>
          <MetricDivider />
          <MetricItem>
            <MetricLabel>Coverage</MetricLabel>
            <MetricValue>{coverageDisplay}</MetricValue>
          </MetricItem>
          <MetricDivider />
          <Tooltip
            title={
              majorDamageRating
                ? `${SUB_DRA_MAPPINGS[majorDamageRating]} - ${majorDamageRating}`
                : 'No damage ratings assigned'
            }>
            <MetricItem>
              <MetricLabel>Damage Rating</MetricLabel>
              <MetricValue>{damageRatingDisplay}</MetricValue>
            </MetricItem>
          </Tooltip>
        </MetricsRow>

        {!isLocked && (
          <ReportAutoSaveIndicator
            autoSaveState={autoSaveState}
            isVisible={!isLoading}
            onRetry={saveNow}
            compact
          />
        )}
      </HeaderBar>

      <StyledForm<SummaryPageData & MainInfo>
        form={form}
        onFinish={handleFormSubmit}
        layout='vertical'
        initialValues={initialValues}
        disabled={formData.lockState?.isLocked}>
        {/* Basic Information Card */}
        <ContentCard title='Basic Information'>
          <FormFieldRow gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item label='Temperature'>
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item name='temperatureUnit' noStyle>
                      <Select
                        options={[
                          {
                            label: temperatureUnitStr,
                            value: temperatureUnitStr,
                          },
                          { label: 'Ambient', value: 'Ambient' },
                        ]}
                        onChange={(value) => {
                          if (isValidTemperatureUnit(value)) {
                            form.setFieldsValue({
                              temperatureUnit: value,
                              surfaceTemperature:
                                value === 'Ambient'
                                  ? undefined
                                  : form.getFieldValue('surfaceTemperature'),
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name='surfaceTemperature' noStyle>
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        step={0.1}
                        disabled={
                          formTempUnit === 'Ambient' ||
                          formData.lockState?.isLocked
                        }
                        placeholder={
                          formTempUnit === 'Ambient' ? 'Ambient' : 'Value'
                        }
                        formatter={(value: number | undefined) => {
                          if (formTempUnit === 'Ambient') {
                            return 'Ambient'
                          }
                          return String(value)
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name='material' label='Material'>
                <Input placeholder='Enter material type' />
              </Form.Item>
            </Col>
            {hasPAUT && (
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name={['weldScanData', 'totalScans']}
                  label='PAUT Weld Scans'
                  tooltip='Number of weld scans to include in the PAUT section'>
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder='Enter count'
                  />
                </Form.Item>
              </Col>
            )}
          </FormFieldRow>
        </ContentCard>

        {/* Inspection Scope Card */}
        <ContentCard title='Inspection Scope'>
          {/* Scope Methods - Collapsible if multiple scan types */}
          {uniqueScanTypes.length > 0 && (
            <CompactFormSection>
              <FormSectionTitle>Scope by Method</FormSectionTitle>
              <FormSectionSubtitle>
                Define the inspection coverage for each scan method. These
                values are used when generating the scope text.
              </FormSectionSubtitle>

              {uniqueScanTypes.length === 1 ? (
                <Form.Item
                  name={['scopesByScanType', uniqueScanTypes[0]]}
                  label={
                    <ScopeMethodLabel>
                      {TYPE_TO_SHORT_NAME[
                        uniqueScanTypes[0] as GeckoInspectionType
                      ] || uniqueScanTypes[0].toUpperCase()}
                    </ScopeMethodLabel>
                  }
                  style={{ marginBottom: 0 }}>
                  <AutoComplete
                    options={defaultInspectionScopes.map((scope) => ({
                      value: scope,
                    }))}
                    placeholder={`Enter scope for ${TYPE_TO_SHORT_NAME[uniqueScanTypes[0]] || uniqueScanTypes[0]}`}
                    filterOption={(inputValue, option) =>
                      option?.value
                        .toUpperCase()
                        .indexOf(inputValue.toUpperCase()) !== -1
                    }>
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                  </AutoComplete>
                </Form.Item>
              ) : (
                <StyledCollapse
                  defaultActiveKey={uniqueScanTypes}
                  items={uniqueScanTypes.map((scanType) => ({
                    key: scanType,
                    label:
                      TYPE_TO_SHORT_NAME[scanType as GeckoInspectionType] ||
                      scanType.toUpperCase(),
                    children: (
                      <Form.Item
                        name={['scopesByScanType', scanType]}
                        style={{ marginBottom: 0 }}>
                        <AutoComplete
                          options={defaultInspectionScopes.map((scope) => ({
                            value: scope,
                          }))}
                          placeholder={`Enter scope for ${TYPE_TO_SHORT_NAME[scanType] || scanType}`}
                          filterOption={(inputValue, option) =>
                            option?.value
                              .toUpperCase()
                              .indexOf(inputValue.toUpperCase()) !== -1
                          }>
                          <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 4 }}
                          />
                        </AutoComplete>
                      </Form.Item>
                    ),
                  }))}
                />
              )}
            </CompactFormSection>
          )}

          {/* Generated Scope Text */}
          <FormSectionTitle>Generated Scope</FormSectionTitle>
          <FormSectionSubtitle>
            Review and edit the generated inspection scope text that will appear
            in the report.
          </FormSectionSubtitle>
          <Form.Item style={{ marginBottom: 0 }}>
            <InspectionScope
              geometricUnit={geometricUnit}
              surfaceTemperature={surfaceTemperature}
              temperatureUnit={formTempUnit}
              material={material}
              scopesByScanType={scopesByScanType}
              initialScope={initialValues?.inspectionScope}
              onChange={handleInspectionScopeChange}
              onGenerate={handleInspectionScopeGenerate}
            />
          </Form.Item>
        </ContentCard>

        {/* Inspection Summary Card */}
        <ContentCard title='Inspection Summary'>
          <Form.Item style={{ marginBottom: 0 }}>
            <InspectionSummary
              initialValues={initialValues.inspectionSummary}
              onChange={handleInspectionSummaryChange}
              onGenerate={handleInspectionSummaryGenerate}
              geometricUnit={geometricUnit}
              measurementPrecision={measurementPrecision}
              hasPAUT={hasPAUT}
            />
          </Form.Item>
        </ContentCard>
      </StyledForm>
    </PageWrapper>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useRautReportOps()
  const data = React.useMemo(
    () => formData.summaryPageData,
    [formData.summaryPageData]
  )
  const { inspectionSummary, inspectionScope } = data

  return (
    <Page size='A4' style={PDFStyles.page} id='summary' wrap={true}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'summary', pageNumber })
          return ''
        }}
      />

      {/* Page header */}
      <PageHeader title='Inspection Summary' />

      <View style={PDFStyles.contentWithPadding}>
        {/* Inspection Scope Section */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionHeader}>Inspection Scope</Text>
          <Text style={PDFStyles.text}>{inspectionScope}</Text>
        </View>

        {/* Inspection Summary Section */}
        {inspectionSummary && (
          <View style={PDFStyles.section}>
            <Text style={PDFStyles.sectionHeader}>Inspection Summary</Text>
            <Text style={PDFStyles.text}>{inspectionSummary.summary}</Text>
          </View>
        )}
      </View>

      {/* Page footer */}
      <PageFooter
        jobNumber={formData.coverPageData.geckoJobId}
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={
          !(
            formData.signoff?.reviewerSignoff &&
            formData.signoff.supervisorSignoff
          )
        }
      />
    </Page>
  )
}

export const Summary: ReportSection = {
  RenderPDF,
  RenderReact,
  required: true,
  id: 'summary',
  description: 'Inspection summary and findings',
  title: 'Summary',
  requiredFields: [],
}
